import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Calendar, DollarSign, TrendingUp, PiggyBank, Users, 
  ChevronRight, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

// Generate years from 2026 to 2045
const YEARS = Array.from({ length: 20 }, (_, i) => 2026 + i);

export default function DetalhamentoPage() {
  const { authAxios } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = async (year) => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/monthly-detail/${year}`);
      setDetail(res.data);
    } catch (err) {
      toast.error("Erro ao carregar detalhamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail(selectedYear);
  }, [selectedYear]);

  const handleYearChange = (value) => {
    setSelectedYear(parseInt(value));
  };

  // Calculate totals
  const totals = detail?.meses?.reduce((acc, m) => ({
    recebido: acc.recebido + m.total_recebido,
    investido: acc.investido + m.total_investido,
    lucro: acc.lucro + m.lucro_bruto,
    clientes: acc.clientes + m.total_clientes
  }), { recebido: 0, investido: 0, lucro: 0, clientes: 0 }) || { recebido: 0, investido: 0, lucro: 0, clientes: 0 };

  return (
    <div className="space-y-8" data-testid="detalhamento-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Detalhamento Mensal</h2>
          <p className="text-muted-foreground">Visão detalhada mês a mês</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Ano:</span>
          <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
            <SelectTrigger className="w-32" data-testid="detail-year-select">
              <SelectValue placeholder="Ano" />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map(year => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Anual Recebido</p>
                    <p className="text-lg font-bold number-display">{formatCurrency(totals.recebido)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <PiggyBank className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Anual Investido</p>
                    <p className="text-lg font-bold number-display">{formatCurrency(totals.investido)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Anual Lucro</p>
                    <p className={`text-lg font-bold number-display ${totals.lucro >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {formatCurrency(totals.lucro)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Anual Clientes</p>
                    <p className="text-lg font-bold number-display">{totals.clientes}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detalhamento de {selectedYear}</CardTitle>
              <CardDescription>Janeiro a Dezembro</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Mês</TableHead>
                      <TableHead className="text-right">Total Recebido</TableHead>
                      <TableHead className="text-right">Total Investido</TableHead>
                      <TableHead className="text-right">Lucro Bruto</TableHead>
                      <TableHead className="text-right">Total Clientes</TableHead>
                      <TableHead className="text-right w-[100px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail?.meses?.map((mes) => {
                      const hasData = mes.total_recebido > 0 || mes.total_investido > 0;
                      const isProfit = mes.lucro_bruto >= 0;
                      
                      return (
                        <TableRow key={mes.mes} className="hover:bg-muted/50">
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              {mes.nome}
                            </div>
                          </TableCell>
                          <TableCell className="text-right number-display text-emerald-500">
                            {formatCurrency(mes.total_recebido)}
                          </TableCell>
                          <TableCell className="text-right number-display text-blue-500">
                            {formatCurrency(mes.total_investido)}
                          </TableCell>
                          <TableCell className={`text-right number-display ${isProfit ? "text-emerald-500" : "text-red-500"}`}>
                            <div className="flex items-center justify-end gap-1">
                              {isProfit ? (
                                <ArrowUpRight className="w-4 h-4" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4" />
                              )}
                              {formatCurrency(mes.lucro_bruto)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right number-display">
                            {mes.total_clientes}
                          </TableCell>
                          <TableCell className="text-right">
                            {hasData ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
                                Com dados
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                Sem dados
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Footer totals */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Total Recebido</p>
                    <p className="text-xl font-bold number-display text-emerald-500">
                      {formatCurrency(totals.recebido)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Total Investido</p>
                    <p className="text-xl font-bold number-display text-blue-500">
                      {formatCurrency(totals.investido)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Total Lucro</p>
                    <p className={`text-xl font-bold number-display ${totals.lucro >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {formatCurrency(totals.lucro)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Total Clientes</p>
                    <p className="text-xl font-bold number-display">
                      {totals.clientes}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Cards Grid (Mobile friendly) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:hidden">
            {detail?.meses?.map((mes) => {
              const hasData = mes.total_recebido > 0 || mes.total_investido > 0;
              const isProfit = mes.lucro_bruto >= 0;
              
              return (
                <Card key={mes.mes} className={`${hasData ? "border-primary/30" : ""}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      {mes.nome}
                      {hasData ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-500">
                          Ativo
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          Vazio
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Recebido</span>
                      <span className="number-display text-emerald-500">{formatCurrency(mes.total_recebido)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Investido</span>
                      <span className="number-display text-blue-500">{formatCurrency(mes.total_investido)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Lucro</span>
                      <span className={`number-display ${isProfit ? "text-emerald-500" : "text-red-500"}`}>
                        {formatCurrency(mes.lucro_bruto)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Clientes</span>
                      <span className="number-display">{mes.total_clientes}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
